import { useState, type ReactNode } from 'react';
import { Check, ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface OpcaoDoFiltro {
  value: string;
  label: string;
}

export interface FiltroDaListaProps {
  /** O nome do eixo — o rótulo do filtro parado ("Tribunal"). */
  eixo: string;
  value: string;
  onValueChange: (value: string) => void;
  opcoes: OpcaoDoFiltro[];
  /** O valor que quer dizer "sem filtro" (ex.: 'todos'). */
  vazio: string;
  /** A opção que tira o filtro, no topo da lista ("Todos os tribunais"). */
  rotuloVazio: string;
  /** Acrescentado ao eixo quando parado — "Vínculo (423)". */
  contagem?: number;
  /** Lista longa: com campo de busca. */
  buscavel?: boolean;
  /** Ícone antes do rótulo (ex.: a ordem). */
  icone?: ReactNode;
  /** Nome acessível do eixo, quando o rótulo parado não o diz (a ordem mostra "Mais recentes"). */
  nome?: string;
  className?: string;
}

/**
 * O filtro das telas de lista (docs/PLANO_BARRA_DE_LISTA.md), com cara de combobox
 * e o mesmo gatilho em toda tela. Parado, o rótulo nomeia o EIXO ("Tribunal");
 * escolhido, mostra o VALOR ("TJAL") e o gatilho fica destacado — quem olha a
 * barra sabe o que está filtrando sem abrir nada. Tirar o filtro é a primeira
 * opção da lista.
 */
export function FiltroDaLista({
  eixo,
  value,
  onValueChange,
  opcoes,
  vazio,
  rotuloVazio,
  contagem,
  buscavel,
  icone,
  nome = eixo,
  className,
}: FiltroDaListaProps) {
  const [aberto, setAberto] = useState(false);
  const ativo = value !== vazio;
  const escolhida = opcoes.find((o) => o.value === value);
  const rotulo = ativo ? (escolhida?.label ?? value) : contagem === undefined ? eixo : `${eixo} (${contagem})`;
  const todas = [{ value: vazio, label: rotuloVazio }, ...opcoes];

  const escolher = (v: string) => {
    onValueChange(v);
    setAberto(false);
  };

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={aberto}
          aria-label={ativo ? `${nome}: ${rotulo}` : nome}
          className={cn(
            'h-11 shrink-0 justify-between gap-2 px-3 font-normal sm:h-9',
            ativo && 'border-primary/50 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary',
            className,
          )}
        >
          {icone}
          <span className="truncate">{rotulo}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(18rem,calc(100vw-2rem))] p-0">
        <Command>
          {buscavel && <CommandInput placeholder={`Buscar ${nome.toLowerCase()}…`} />}
          <CommandList>
            <CommandEmpty>Nada encontrado.</CommandEmpty>
            {todas.map((o) => (
              <CommandItem
                key={o.value}
                // O cmdk filtra e navega pelo `value`: o rótulo é o que a pessoa digita.
                value={`${o.label} ${o.value}`}
                onSelect={() => escolher(o.value)}
                className="min-h-10 cursor-pointer"
              >
                <Check className={cn('mr-2 h-4 w-4', o.value === value ? 'opacity-100' : 'opacity-0')} aria-hidden />
                {o.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
